import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Database, Gauge, ShieldAlert, UserMinus } from 'lucide-react';
import {
  useApiUsage,
  useCreatorDataFlags,
  useCreatorDataRequests,
  useExtractorHealth,
  usePoolStats,
  useResolveCreatorDataFlag,
  useResolveCreatorDataRequest,
} from '../../hooks/admin/useAdminDataSources';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/utils';
import StatTile, { type StatTone } from '../../components/admin/StatTile';
import DataTable from '../../components/ui/DataTable';
import Pagination from '../../components/admin/Pagination';
import InlineNotice from '../../components/admin/InlineNotice';
import { Pill } from '../../components/admin/StatusBadge';
import type { ExtractorPlatformHealth } from '../../types/admin';

/**
 * Admin → Data sources.
 *
 * The operational view of the creator-data pipeline. It answers two questions that otherwise have
 * no answer until an agency complains: is the extension still reading pages correctly, and how
 * much of today's YouTube quota is left.
 */

const WINDOW_OPTIONS = [
  { value: 6, label: '6h' },
  { value: 24, label: '24h' },
  { value: 72, label: '3d' },
  { value: 168, label: '7d' },
] as const;

const PAGE_SIZE = 25;

/** Below this a platform's extractor is treated as broken rather than merely lossy. */
const DEGRADED_BELOW = 0.6;
/** Between this and DEGRADED_BELOW it is worth looking at but not yet an emergency. */
const WARN_BELOW = 0.85;

function rateTone(rate: number | null): StatTone {
  if (rate === null) return 'muted';
  if (rate < DEGRADED_BELOW) return 'danger';
  if (rate < WARN_BELOW) return 'warn';
  return 'pos';
}

/**
 * "No captures" and "every capture failed" must never render the same.
 *
 * A dash reads as absence and a red 0% reads as breakage, and confusing the two would send someone
 * hunting a bug on a platform nobody has browsed today.
 */
function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}

function platformLabel(platform: string): string {
  return platform.charAt(0) + platform.slice(1).toLowerCase();
}

export default function AdminDataSources() {
  const [hours, setHours] = useState<number>(24);
  const [flagStatus, setFlagStatus] = useState<string>('OPEN');
  const [flagPage, setFlagPage] = useState(1);
  const [requestStatus, setRequestStatus] = useState<string>('PENDING_VERIFICATION');
  const [requestPage, setRequestPage] = useState(1);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const health = useExtractorHealth(hours);
  const usage = useApiUsage(14);
  const pool = usePoolStats();
  const flags = useCreatorDataFlags({ status: flagStatus, page: flagPage, limit: PAGE_SIZE });
  const resolveFlag = useResolveCreatorDataFlag();
  const requests = useCreatorDataRequests({
    status: requestStatus,
    page: requestPage,
    limit: PAGE_SIZE,
  });
  const resolveRequest = useResolveCreatorDataRequest();

  const degraded = (health.data?.platforms ?? []).filter(
    (p) => p.successRate !== null && p.successRate < DEGRADED_BELOW && p.attempts > 0
  );

  const youtube = usage.data?.youtube;
  const igDiscovery = usage.data?.instagramDiscovery;

  // DataTable keys rows by `id`. A (platform, version) pair is the natural identity here, so it is
  // the key rather than an array index — which would re-associate rows as the sort order changes.
  const versionRows = (health.data?.platforms ?? [])
    .flatMap((p) => p.versions)
    .map((v) => ({ ...v, id: `${v.platform}|${v.extractorVersion}` }));

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-glass-text">Data sources</h1>
          <p className="text-sm text-glass-text-3">
            Extractor health, creator pool and external API budgets.
          </p>
        </div>
        <div className="flex gap-1" role="group" aria-label="Time window">
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setHours(opt.value)}
              aria-pressed={hours === opt.value}
              className={`rounded-md px-3 py-1.5 text-sm ${
                hours === opt.value
                  ? 'bg-accent2/20 text-accent2'
                  : 'text-glass-text-3 hover:text-glass-text'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </header>

      {health.isError && (
        <InlineNotice tone="error">{getApiErrorMessage(health.error)}</InlineNotice>
      )}

      {degraded.length > 0 && (
        <InlineNotice tone="error">
          <span className="font-medium">
            {degraded.map((p) => platformLabel(p.platform)).join(', ')}{' '}
            {degraded.length === 1 ? 'is' : 'are'} failing.
          </span>{' '}
          This usually means the platform changed its page structure. Ship a fixed extractor, or set
          EXTENSION_MIN_VERSION above the current build to stop it capturing while you do.
        </InlineNotice>
      )}

      {health.data?.minVersion && (
        <InlineNotice tone="warn">
          Capture is gated to extension {health.data.minVersion} and above. Older builds are refused.
        </InlineNotice>
      )}

      <section aria-labelledby="extractors-heading" className="space-y-3">
        <h2 id="extractors-heading" className="flex items-center gap-2 text-sm font-medium text-glass-text-2">
          <Gauge className="h-4 w-4" aria-hidden="true" />
          Extractor success rate — last {hours}h
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {(health.data?.platforms ?? []).map((p: ExtractorPlatformHealth) => (
            <StatTile
              key={p.platform}
              label={platformLabel(p.platform)}
              value={formatRate(p.successRate)}
              sub={
                p.attempts === 0
                  ? 'no captures'
                  : `${p.successes.toLocaleString()} of ${p.attempts.toLocaleString()}`
              }
              tone={rateTone(p.successRate)}
              loading={health.isLoading}
              title={
                p.attempts === 0
                  ? 'Nobody captured on this platform in the window — not a failure.'
                  : undefined
              }
            />
          ))}
        </div>
      </section>

      <section aria-labelledby="versions-heading" className="space-y-3">
        <h2 id="versions-heading" className="text-sm font-medium text-glass-text-2">
          By extractor version
        </h2>
        <p className="text-xs text-glass-text-3">
          Broken out per version so a bad release is visible against a good one rather than blended
          into one sagging average. Rows written by a suspect version can be found with that exact
          value in <code>extractorVersion</code>.
        </p>
        <DataTable
          data={versionRows}
          isLoading={health.isLoading}
          emptyMessage="No captures recorded in this window."
          columns={[
            { header: 'Platform', accessor: (row) => platformLabel(row.platform) },
            { header: 'Version', accessor: (row) => <code className="text-xs">{row.extractorVersion}</code> },
            { header: 'Attempts', accessor: (row) => row.attempts.toLocaleString() },
            { header: 'Successes', accessor: (row) => row.successes.toLocaleString() },
            {
              header: 'Rate',
              accessor: (row) => (
                <span className={row.successRate < DEGRADED_BELOW ? 'text-[rgb(var(--danger))]' : ''}>
                  {formatRate(row.successRate)}
                </span>
              ),
            },
          ]}
        />
      </section>

      <section aria-labelledby="budget-heading" className="space-y-3">
        <h2 id="budget-heading" className="flex items-center gap-2 text-sm font-medium text-glass-text-2">
          <Database className="h-4 w-4" aria-hidden="true" />
          External API budgets
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile
            label="YouTube units today"
            value={youtube ? `${youtube.used.toLocaleString()} / ${youtube.budget.toLocaleString()}` : '—'}
            sub={youtube ? `${youtube.remaining.toLocaleString()} left` : undefined}
            tone={youtube?.exhausted ? 'danger' : youtube && youtube.remaining < 1_000 ? 'warn' : 'pos'}
            loading={usage.isLoading}
            // The quota day is the provider's, not ours — worth saying, because "today" here does
            // not roll over at the same moment as everything else on this page.
            title={youtube ? `Quota day ${youtube.day} (${youtube.quotaTimeZone})` : undefined}
          />
          <StatTile
            label="Instagram lookups today"
            value={igDiscovery ? `${igDiscovery.used.toLocaleString()} / ${igDiscovery.budget.toLocaleString()}` : '—'}
            sub={igDiscovery ? `${igDiscovery.remaining.toLocaleString()} left` : undefined}
            tone={igDiscovery?.exhausted ? 'danger' : igDiscovery && igDiscovery.remaining < 100 ? 'warn' : 'pos'}
            loading={usage.isLoading}
            // Unlike YouTube's, this ceiling is one we chose. Meta rate-limits business_discovery on
            // a rolling hour that cannot be queried, so this number governs our own spending and
            // says nothing about whether Meta will accept the next call.
            title={
              igDiscovery
                ? `Our own daily cap, not Meta's limit. Day ${igDiscovery.day} (${igDiscovery.quotaTimeZone})`
                : undefined
            }
          />
          <StatTile
            label="Creators in pool"
            value={pool.data?.identities?.toLocaleString() ?? '—'}
            sub={pool.data ? `${pool.data.accounts.toLocaleString()} platform accounts` : undefined}
            loading={pool.isLoading}
          />
          <StatTile
            label="Snapshots"
            value={pool.data?.snapshots?.toLocaleString() ?? '—'}
            loading={pool.isLoading}
          />
          <StatTile
            label="Contributing agencies"
            value={pool.data?.contributingAgencies?.toLocaleString() ?? '—'}
            sub="opted in to the shared pool"
            tone="accent"
            loading={pool.isLoading}
          />
        </div>
      </section>

      <section aria-labelledby="flags-heading" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="flags-heading" className="flex items-center gap-2 text-sm font-medium text-glass-text-2">
            <ShieldAlert className="h-4 w-4" aria-hidden="true" />
            Creator data flags
          </h2>
          <div className="flex gap-1" role="group" aria-label="Flag status">
            {['OPEN', 'QUARANTINED', 'ALL'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setFlagStatus(s);
                  setFlagPage(1);
                }}
                aria-pressed={flagStatus === s}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  flagStatus === s ? 'bg-accent2/20 text-accent2' : 'text-glass-text-3 hover:text-glass-text'
                }`}
              >
                {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-glass-text-3">
          Reviewed by hand on purpose: the flags that matter most are the ones a rule could not
          judge. <strong>Quarantine</strong> stops the creator being adopted or promoted at all —
          right for data we cannot vouch for, wrong for a merely surprising number.
        </p>

        {resolveFlag.isError && (
          <InlineNotice tone="error">{getApiErrorMessage(resolveFlag.error)}</InlineNotice>
        )}

        <DataTable
          data={flags.data?.flags ?? []}
          isLoading={flags.isLoading}
          emptyMessage={
            flagStatus === 'OPEN' ? 'No open flags. The pool is clean.' : 'No flags to show.'
          }
          columns={[
            { header: 'Raised', accessor: (row) => formatDateTime(row.createdAt) },
            { header: 'Reason', accessor: (row) => <Pill>{row.reason}</Pill> },
            {
              header: 'Account',
              accessor: (row) => (
                <code className="text-xs text-glass-text-3">{row.platformAccountId}</code>
              ),
            },
            {
              header: 'Contributor',
              accessor: (row) =>
                row.reportedByOrgId ? (
                  <code className="text-xs text-glass-text-3">{row.reportedByOrgId}</code>
                ) : (
                  // A server-side source (the YouTube sync) has no contributing agency, and
                  // showing a blank cell would read as missing data rather than "not applicable".
                  <span className="text-glass-text-3">server</span>
                ),
            },
            {
              header: 'Status',
              accessor: (row) =>
                row.status === 'OPEN' ? (
                  <span className="inline-flex items-center gap-1 text-[rgb(var(--warn))]">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> Open
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-glass-text-3">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
                  </span>
                ),
            },
            {
              header: '',
              accessor: (row) =>
                row.status === 'OPEN' ? (
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="text-xs text-glass-text-3 underline underline-offset-2 hover:text-glass-text"
                      disabled={resolveFlag.isPending}
                      onClick={() => resolveFlag.mutate({ id: row.id, status: 'REJECTED' })}
                    >
                      Dismiss
                    </button>
                    <button
                      type="button"
                      className="text-xs text-[rgb(var(--danger))] underline underline-offset-2"
                      disabled={resolveFlag.isPending}
                      onClick={() => resolveFlag.mutate({ id: row.id, status: 'QUARANTINED' })}
                    >
                      Quarantine
                    </button>
                  </div>
                ) : null,
            },
          ]}
        />

        <Pagination
          pagination={flags.data}
          onPageChange={setFlagPage}
          label="flags"
          isFetching={flags.isFetching}
        />
      </section>

      <section aria-labelledby="creator-requests-heading" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2
            id="creator-requests-heading"
            className="flex items-center gap-2 text-sm font-medium text-glass-text-2"
          >
            <UserMinus className="h-4 w-4" aria-hidden="true" />
            Creator removal requests
          </h2>
          <div className="flex gap-1" role="group" aria-label="Request status">
            {['PENDING_VERIFICATION', 'APPLIED', 'ALL'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setRequestStatus(s);
                  setRequestPage(1);
                }}
                aria-pressed={requestStatus === s}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  requestStatus === s
                    ? 'bg-accent2/20 text-accent2'
                    : 'text-glass-text-3 hover:text-glass-text'
                }`}
              >
                {s === 'ALL' ? 'All' : s === 'PENDING_VERIFICATION' ? 'Pending' : 'Applied'}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-glass-text-3">
          Creators prove control by putting a code in their bio, and most requests resolve
          themselves. These are the ones that could not: a login wall, a cached page, a creator who
          cannot edit their bio. <strong>Confirm control yourself before removing</strong> — a DM
          from the account, or an email from the domain in the bio. Removal deletes every recorded
          reading and cannot be undone.
        </p>

        {resolveRequest.isError && (
          <InlineNotice tone="error">{getApiErrorMessage(resolveRequest.error)}</InlineNotice>
        )}

        <DataTable
          data={requests.data?.requests ?? []}
          isLoading={requests.isLoading}
          emptyMessage={
            requestStatus === 'PENDING_VERIFICATION'
              ? 'Nothing waiting on a person.'
              : 'No requests to show.'
          }
          columns={[
            { header: 'Asked', accessor: (row) => formatDateTime(row.requestedAt) },
            {
              header: 'Account',
              accessor: (row) => (
                <span className="text-glass-text">
                  {platformLabel(row.platform)} · @{row.handleNormalized}
                </span>
              ),
            },
            {
              header: 'Tries',
              accessor: (row) => (
                // A creator who has tried repeatedly is stuck on something, and that is precisely
                // the person this list exists for.
                <span className={row.attempts >= 3 ? 'text-[rgb(var(--warn))]' : 'text-glass-text-3'}>
                  {row.attempts}
                </span>
              ),
            },
            {
              header: 'Status',
              accessor: (row) =>
                row.status === 'PENDING_VERIFICATION' ? (
                  <span className="inline-flex items-center gap-1 text-[rgb(var(--warn))]">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> Pending
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-glass-text-3">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
                    {/* How control was proven, not just that it was: an APPLIED row is very
                        different depending on whether a machine or a person decided it. */}
                    {row.verifiedVia ? ` · ${row.verifiedVia.replace(/_/g, ' ')}` : ''}
                  </span>
                ),
            },
            {
              header: '',
              accessor: (row) => {
                if (row.status !== 'PENDING_VERIFICATION') return null;
                // Two steps, because this one deletes data and there is no undo. The flags above
                // are reversible; this is not.
                if (confirmingId === row.id) {
                  return (
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="text-xs text-glass-text-3 underline underline-offset-2 hover:text-glass-text"
                        onClick={() => setConfirmingId(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="text-xs font-medium text-[rgb(var(--danger))] underline underline-offset-2"
                        disabled={resolveRequest.isPending}
                        onClick={() => {
                          resolveRequest.mutate({ id: row.id, status: 'APPLIED' });
                          setConfirmingId(null);
                        }}
                      >
                        Yes, delete their data
                      </button>
                    </div>
                  );
                }
                return (
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="text-xs text-glass-text-3 underline underline-offset-2 hover:text-glass-text"
                      disabled={resolveRequest.isPending}
                      onClick={() => resolveRequest.mutate({ id: row.id, status: 'REJECTED' })}
                    >
                      Not genuine
                    </button>
                    <button
                      type="button"
                      className="text-xs text-[rgb(var(--danger))] underline underline-offset-2"
                      disabled={resolveRequest.isPending || !row.platformAccountId}
                      title={
                        row.platformAccountId
                          ? undefined
                          : 'We hold no pool data for that handle, so there is nothing to remove.'
                      }
                      onClick={() => setConfirmingId(row.id)}
                    >
                      Remove
                    </button>
                  </div>
                );
              },
            },
          ]}
        />

        <Pagination
          pagination={requests.data}
          onPageChange={setRequestPage}
          label="requests"
          isFetching={requests.isFetching}
        />
      </section>
    </div>
  );
}
